import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  BookOpen, 
  Plus, 
  Eye, 
  Share2,
  Lock,
  Calendar,
  Lightbulb,
  TrendingUp,
  Heart
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ReflexoesPessoais = ({ pdiId, colaboradorId }) => {
  const [reflexoes, setReflexoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [novaReflexao, setNovaReflexao] = useState({
    titulo: '',
    conteudo: '',
    competencia_relacionada: '',
    compartilhar_com_gestor: false
  });

  // Gerar pdiId automático se não existir
  const effectivePdiId = pdiId || `pdi_${colaboradorId}_reflexoes`;

  useEffect(() => {
    if (effectivePdiId && colaboradorId) {
      loadReflexoes();
    }
  }, [effectivePdiId, colaboradorId]);

  const loadReflexoes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/pdi-completo/${effectivePdiId}/reflexoes`);
      setReflexoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar reflexões:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!colaboradorId) {
      toast.error('Colaborador não informado');
      return;
    }

    try {
      setLoading(true);
      
      await axios.post(`${API}/api/pdi-completo/reflexoes/criar`, null, {
        params: {
          colaborador_id: colaboradorId,
          pdi_id: effectivePdiId,
          titulo: novaReflexao.titulo,
          conteudo: novaReflexao.conteudo,
          competencia_relacionada: novaReflexao.competencia_relacionada || undefined,
          compartilhar_com_gestor: novaReflexao.compartilhar_com_gestor
        }
      });

      toast.success('Reflexão salva com sucesso! 🎉');
      
      setNovaReflexao({
        titulo: '',
        conteudo: '',
        competencia_relacionada: '',
        compartilhar_com_gestor: false
      });
      setMostrarFormulario(false);
      
      await loadReflexoes();
    } catch (error) {
      console.error('Erro ao salvar reflexão:', error);
      toast.error('Erro ao salvar reflexão');
    } finally {
      setLoading(false);
    }
  };

  const perguntasGuiadas = [
    "O que aprendi com este ciclo?",
    "Quais foram meus maiores desafios?",
    "Como superei as dificuldades?",
    "Que competências desenvolvi?",
    "O que farei diferente no próximo ciclo?",
    "Quais foram meus principais resultados?",
    "Como posso aplicar o que aprendi?"
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-purple-600 rounded-full">
                <BookOpen className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-1">Diário de Desenvolvimento</h2>
                <p className="text-gray-600">
                  Espaço para suas reflexões pessoais e aprendizados
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setMostrarFormulario(!mostrarFormulario)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Reflexão
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Formulário de Nova Reflexão */}
      {mostrarFormulario && (
        <Card className="border-2 border-purple-300">
          <CardHeader className="bg-purple-50">
            <CardTitle className="flex items-center space-x-2">
              <Lightbulb className="h-5 w-5 text-purple-600" />
              <span>Nova Reflexão</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Perguntas Guiadas */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-purple-700">
                  💡 Perguntas Guiadas (clique para usar como título)
                </label>
                <div className="flex flex-wrap gap-2">
                  {perguntasGuiadas.map((pergunta, idx) => (
                    <Button
                      key={idx}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs hover:bg-purple-100 hover:border-purple-300"
                      onClick={() => setNovaReflexao({...novaReflexao, titulo: pergunta})}
                    >
                      {pergunta}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Título */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Título da Reflexão
                </label>
                <Input
                  value={novaReflexao.titulo}
                  onChange={(e) => setNovaReflexao({...novaReflexao, titulo: e.target.value})}
                  placeholder="Ex: O que aprendi com este ciclo?"
                  required
                />
              </div>

              {/* Conteúdo */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Sua Reflexão
                </label>
                <Textarea
                  value={novaReflexao.conteudo}
                  onChange={(e) => setNovaReflexao({...novaReflexao, conteudo: e.target.value})}
                  placeholder="Escreva aqui suas reflexões, aprendizados e insights..."
                  rows={6}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Seja honesto e específico. Esta reflexão ajudará você a acompanhar sua evolução.
                </p>
              </div>

              {/* Competência Relacionada (opcional) */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Competência Relacionada (opcional)
                </label>
                <Input
                  value={novaReflexao.competencia_relacionada}
                  onChange={(e) => setNovaReflexao({...novaReflexao, competencia_relacionada: e.target.value})}
                  placeholder="Ex: Liderança, Comunicação, Resolução de Problemas"
                />
              </div>

              {/* Compartilhar com Gestor */}
              <div className="flex items-center space-x-2 p-4 bg-blue-50 rounded-lg">
                <input
                  type="checkbox"
                  id="compartilhar"
                  checked={novaReflexao.compartilhar_com_gestor}
                  onChange={(e) => setNovaReflexao({...novaReflexao, compartilhar_com_gestor: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="compartilhar" className="text-sm flex items-center space-x-2 cursor-pointer">
                  {novaReflexao.compartilhar_com_gestor ? (
                    <>
                      <Share2 className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">Compartilhar com meu gestor</span>
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 text-gray-600" />
                      <span>Manter privado (apenas eu vejo)</span>
                    </>
                  )}
                </label>
              </div>

              {/* Botões */}
              <div className="flex space-x-3 pt-4">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {loading ? 'Salvando...' : 'Salvar Reflexão'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setMostrarFormulario(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Histórico de Reflexões */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            <span>Histórico de Reflexões</span>
            <Badge className="bg-purple-100 text-purple-700">
              {reflexoes.length} reflexões
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reflexoes.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">Nenhuma reflexão registrada ainda</p>
              <p className="text-sm text-gray-400 mb-4">
                Comece seu diário de desenvolvimento clicando em "Nova Reflexão"
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reflexoes.map((reflexao) => (
                <Card 
                  key={reflexao.id} 
                  className="hover:shadow-md transition-shadow border-l-4 border-l-purple-500"
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg mb-1">{reflexao.titulo}</h4>
                        <div className="flex items-center space-x-3 text-sm text-gray-600">
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(reflexao.criado_em)}
                          </span>
                          {reflexao.competencia_relacionada && (
                            <Badge variant="outline" className="text-xs">
                              {reflexao.competencia_relacionada}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {reflexao.compartilhar_com_gestor ? (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            <Share2 className="h-3 w-3 mr-1" />
                            Compartilhado
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-700 text-xs">
                            <Lock className="h-3 w-3 mr-1" />
                            Privado
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {reflexao.conteudo}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dicas */}
      <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <Lightbulb className="h-5 w-5 text-yellow-600 mt-1" />
            <div>
              <h4 className="font-semibold mb-2 text-yellow-800">💡 Dicas para reflexões eficazes:</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Seja específico sobre o que você aprendeu</li>
                <li>• Descreva situações concretas que vivenciou</li>
                <li>• Reflita sobre como aplicar o aprendizado no futuro</li>
                <li>• Reconheça seus progressos, mesmo os pequenos</li>
                <li>• Identifique áreas que ainda precisa desenvolver</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReflexoesPessoais;
